import React, { useMemo, useEffect, useState, useContext } from 'react'
import { Form, Col, Row, InputGroup } from 'react-bootstrap'
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete'
import { TextField, MuiThemeProvider, Popper } from '@material-ui/core'
import { createTheme } from '@material-ui/core/styles'
import objectPath from 'object-path'
import { useRouteMatch, useLocation } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'

import { ConfigContext } from 'contexts/ConfigContext'
import CircularProgress from '@mui/material/CircularProgress'
import httpClient from 'utility/httpClient.util'
import { debounceCallback } from 'utility/debounceCallback.util'

import {
  deleteData,
  getData,
  postData,
  putData,
  setApi,
} from 'designer/component/action/Api'
import InputPrepend from '../InputPrepend'
import InputAppend from '../InputAppend'
import {
  compareTwoValue,
  formatNumber,
  getFieldValue,
  getRowFieldValue,
  getTitleValue,
  isNumber,
  truncateDecimal,
} from 'designer/component/action/Data'
import { onUpdateCondition } from 'designer/component/action/condition/Action'
import {
  checkOnEnter,
  handleEnterAction,
} from 'designer/component/action/Action'
import { isEmpty } from 'designer/component/action/condition/State'

const autocompleteRequestCache = {}
const autocompleteRequestCacheTime = 1500

const getAutocompleteRequestCacheKey = (url = '', dataKey = '') => {
  return [url, dataKey].join('|')
}

const getAutocompleteRequestCache = (cacheKey = '') => {
  let cache = autocompleteRequestCache[cacheKey]
  if (!cache) return null

  if (cache.promise) return cache.promise
  if (Date.now() - cache.createdAt <= autocompleteRequestCacheTime) {
    return Promise.resolve(cache.data)
  }

  delete autocompleteRequestCache[cacheKey]
  return null
}

const clearAutocompleteRequestCache = () => {
  Object.keys(autocompleteRequestCache).forEach((cacheKey) => {
    let cache = autocompleteRequestCache[cacheKey]
    if (cache?.promise) return
    if (Date.now() - cache.createdAt <= autocompleteRequestCacheTime) return

    delete autocompleteRequestCache[cacheKey]
  })
}

const getAutocompleteResponseData = async (url = '', dataKey = '') => {
  clearAutocompleteRequestCache()

  let cacheKey = getAutocompleteRequestCacheKey(url, dataKey)
  let cache = getAutocompleteRequestCache(cacheKey)
  if (cache) return cache

  let request = httpClient
    .get(url)
    .then(({ data }) => objectPath.get(data, dataKey))
    .catch(() => null)

  autocompleteRequestCache[cacheKey] = {
    promise: request,
    createdAt: Date.now(),
  }

  let responseData = await request
  if (responseData === null) {
    delete autocompleteRequestCache[cacheKey]
  } else {
    autocompleteRequestCache[cacheKey] = {
      data: responseData,
      createdAt: Date.now(),
    }
  }

  return responseData
}

const AutocompleteContent = (props) => {
  let {
    value,
    uischema,
    config = {},
    labelField,
    setLabelField,
    options,
    setOptions,
    updateData,
    updateMapOther,
    onUpdateRowData,
    rowUpdate,
    isFromTable,
    setIsOnChange,
    fieldName,
    fieldKey,
    tableColumn,
    data,
    error,
    handleKeyDown = () => {},
    isRequire,
    showRequire: propShowRequire,
  } = props
  let {
    isMulti,
    optionType = '',
    isAll,
    allLabel,
    allValue,
    key,
    prependIcon,
    appendIcon,
    prependIconTooltip = null,
    appendIconTooltip = null,
    mapOther,
  } = uischema

  const dataValue = isFromTable ? rowUpdate : data
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    dynamicKey,
    dynamicData,
  } = config

  const match = useRouteMatch()
  const location = useLocation()
  const search = location.search || []
  const pathParams = match.params || {}
  const urlSearchParams = new URLSearchParams(search)
  const queryParams = Object.fromEntries([...urlSearchParams]) || {}
  const allParams = { ...pathParams, ...queryParams }

  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    elementAction,
    setElementAction,
    setFieldFormData,
    getCurrentUi,
    isLoadingApi,
    isUiChanged,
    setIsUiChanged,
    searchData,
  } = designContext
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const formData = jsonForm.formData
  const showRequire = propShowRequire ?? (isRequire && isEmpty(value))

  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  const { joinTitle = '' } = uischema
  let hasOnBlur = objectPath.has(uischema, 'onBlur')

  let { jsonLabel, jsonValue } = uischema
  const list = objectPath.get(uischema, `${optionType}`) || {}
  const {
    limit = null,
    dataKey = '',
    valueKey = {},
    isUseLabelPattern = true,
    fieldTitle,
    keyOptions,
    isCustomInput,
  } = list
  const optionValue = jsonValue ?? list.optionValue ?? 'value'

  const titleValue = uischema?.titleValue || list?.titleValue
  const isJsonOption = optionType === 'jsonField'
  const valueKeyList = Object.keys(valueKey)
  const [isInput, setIsInput] = useState(false)

  const [loading, setLoading] = useState(false)
  const [otherParamUpdated, setOtherParamUpdated] = useState({})

  const getOptions = (options, isReturn) => {
    let newOptions = isAll
      ? [
          {
            label: allLabel,
            [optionValue]: allValue,
            isAll: true,
          },
        ]
      : []
    if (isJsonOption) {
      newOptions = getJsonField()
    } else if (Array.isArray(options)) {
      newOptions = newOptions.concat(options)
    } else {
      newOptions = []
    }

    if (Array.isArray(titleValue)) {
      newOptions.map((option) => {
        let titleList = []
        let fontColor = ''
        let isLockedSelect
        titleValue.forEach(
          ({ title, valueType, thousandSeparator, precision = 0 }) => {
            let subTitle = []
            title.forEach((title) => {
              let labelValue = title.value
              let isNew = objectPath.get(title, '__isNew__')
              if (!isNew) {
                let hasValue = objectPath.has(option, title.value)
                let value = objectPath.get(option, title.value)
                if (valueType === 'number') {
                  let value = objectPath.get(option, title.value) ?? 0
                  let format = thousandSeparator
                    ? formatNumber(value, precision)
                    : truncateDecimal(value, precision, true)
                  labelValue = format.toString()
                } else if (hasValue && value) {
                  labelValue = objectPath.get(option, title.value)
                } else {
                  labelValue = ''
                }
              }
              subTitle.push(labelValue)
            })
            titleList.push(subTitle.join(''))
          },
        )
        let labelValue = titleList.join(joinTitle)
        objectPath.set(option, `@${optionValue}`, labelValue)
        objectPath.set(option, '@fontColor', fontColor)
        if (isLockedSelect) {
          objectPath.set(option, 'isDisabled', fontColor)
        }
        return option
      })
    }
    if (isReturn) {
      return newOptions
    }

    setOptions(newOptions)
  }

  useEffect(() => {
    getOptions(list?.options)
  }, [list?.options, isAll, allLabel, allValue])

  const getInternal = async (keyword, isReturn, optionData) => {
    setLoading(true)
    let responseData
    let designerData = {}
    optionData = optionData ?? list

    let url = setApi(
      {
        ...optionData,
        apiType: 'internal',
        isOption: true,
        jsonForm,
      },
      {
        ...formData,
        ...rowUpdate,
        [uischema.key]: keyword,
        [uischema.field]: keyword,
        [uischema.name]: keyword,
      },
      {
        searchData,
        allParams,
        designerData,
        keyword: {
          [uischema.key || uischema.field]: keyword,
          [uischema.name]: keyword,
        },
      },
    )

    if (url) {
      responseData = await getAutocompleteResponseData(url, dataKey)
      getOptions(responseData)
    }
    setLoading(false)
    if (isReturn) {
      return responseData
    }
  }

  const getExternal = async (keyword, isReturn, optionData) => {
    setLoading(true)
    let designerData = {}
    let responseData
    optionData = optionData ?? list
    let url = setApi(
      {
        ...optionData,
        apiType: 'external',
        api: list.api || list.url,
        jsonForm,
      },
      {
        ...formData,
        ...rowUpdate,
        [uischema.key]: keyword,
        [uischema.field]: keyword,
      },
      {
        allParams,
        designerData,
        keyword: {
          [uischema.key || uischema.field]: keyword,
        },
      },
    )
    if (url) {
      responseData = await getAutocompleteResponseData(url, dataKey)
      getOptions(responseData)
      return responseData
    }
    setLoading(false)
    if (isReturn) {
      return responseData
    }
  }

  const getJsonField = (keyword) => {
    let options
    if (isDynamic) {
      options = objectPath.get(dynamicData, list)
    } else {
      options = objectPath.get(jsonForm.formData, list)
    }
    options = options || []
    // let newOptions = options.map((option) => {
    //   if (typeof option === 'object') return option
    //   objectPath.set()
    //   return option
    // })
    return options
  }

  const handleInputPath = (keyword = '') => {
    if (optionType === 'core') {
      getInternal(keyword)
    } else if (optionType === 'internal') {
      getInternal(keyword)
    } else if (optionType === 'external') {
      getExternal(keyword)
    } else if (isJsonOption) {
      getJsonField(keyword)
    }
  }

  const getAutocompleteValue = async (
    keyword,
    { isAutoCreate = true, isReturn = false, optionData, isOption = false },
  ) => {
    let options

    if (optionType === 'core') {
      options = await getInternal(keyword, true, optionData)
    } else if (optionType === 'internal') {
      options = await getInternal(keyword, true, optionData)
    } else if (optionType === 'external') {
      options = await getExternal(keyword, true, optionData)
    } else if (isJsonOption) {
      options = getJsonField(keyword)
    }
    if (isOption) {
      return options
    }
    if (options) {
      options = getOptions(options, true)
      let option = options.find(
        (item) =>
          objectPath.get(item, optionValue) === keyword ||
          objectPath.get(item, optionValue) == keyword,
      )
      if (isReturn) {
        return option
      }
      if (isAutoCreate) {
        if (option) {
          handleSelectPath(option)
        }
        return option
      }
    }
    if (isCustomInput) {
      let option = {}
      objectPath.set(option, optionValue, keyword)
      handleSelectPath(option)
      return option
    }
    return {}
  }

  const callAndUpdateOtherValues = async (keyword) => {
    let responseData

    let hasValue = objectPath.get(dataValue, optionValue)
    let hasTitle = createLabelField(dataValue)
    if (hasValue && hasTitle && keyword === hasValue) {
      handleSelectPath(dataValue)
      updateOtherParam(dataValue)
    } else {
      if (optionType === 'core') {
        responseData = await getInternal(keyword, true)
      } else if (optionType === 'internal') {
        responseData = await getInternal(keyword, true)
      } else if (optionType === 'external') {
        responseData = await getExternal(keyword, true)
      } else if (isJsonOption) {
        responseData = getJsonField(keyword)
      }
      if (responseData) {
        let autocomplete = objectPath.get(responseData, [0]) || {}
        updateOtherParam(autocomplete)
      }
    }
  }

  const updateMapRowOther = (option) => {
    if (mapOther) {
      mapOther.forEach(async ({ field, value }) => {
        let column = tableColumn.find((item) => item.field === field)
        if (column) {
          let { typeField, columnValue } = column
          if (typeField === 'autocomplete') {
            let otherValue = getRowFieldValue(column, option)
            objectPath.set(rowUpdate, `@${field}`, otherValue)
            let fieldValue = objectPath.get(option, field)
            if (field) {
              updateData(fieldValue, `${field}`)
            }
            if (columnValue) {
              updateData(otherValue, `@${columnValue}`)
            }
          } else {
            let otherValue
            if (objectPath.has(option, value)) {
              otherValue = objectPath.get(option, value)
            } else {
              otherValue = objectPath.get(option, field)
            }
            if (objectPath.has(rowUpdate, field)) {
              objectPath.set(rowUpdate, field, otherValue)
              updateData(otherValue, field)
            }
          }
        }
      })
      onUpdateRowData(rowUpdate)
    }
  }

  const handleSelectPath = (option, freeSolo) => {
    if (option) {
      if (isMulti) {
        let newData = []
        option.forEach((item) => {
          let value
          if (isJsonOption) {
            value = createLabelOption(option)
          } else if (isUseLabelPattern) {
            value = createLabelOption(option)
          } else {
            value = objectPath.get(item, optionValue)
          }
          newData.push(value)
        })
        if (isFromTable) {
          objectPath.set(rowUpdate, fieldName, newData)
          updateData(newData, fieldName)
          onUpdateRowData(rowUpdate)
        } else {
          updateData(newData)
        }
        setLabelField(newData)
      } else {
        if (freeSolo) {
          value = option
        } else {
          value = objectPath.get(option, optionValue)
        }
        if (isFromTable) {
          rowUpdate = {
            ...rowUpdate,
            ...option,
          }
          objectPath.set(rowUpdate, fieldName, value)
          updateData(value, fieldName)
          onUpdateRowData(rowUpdate)
        } else {
          updateData(value)
        }
        createLabelField(option)
      }
    } else {
      if (isFromTable) {
        objectPath.set(rowUpdate, fieldName, option)
        let keyOptions = list.keyOptions
        if (keyOptions) {
          keyOptions.forEach((key) => {
            if (key.startsWith('@')) return
            if (objectPath.has(rowUpdate, `@${key}`)) {
              objectPath.set(rowUpdate, `@${key}`, option)
            }
            if (objectPath.has(rowUpdate, key)) {
              objectPath.set(rowUpdate, key, option)
            }
          })
        }
        updateData(option, fieldName)
        onUpdateRowData(option)
      } else {
        updateData(option)
      }
      setLabelField(option)
    }
    setLoading(false)
    if (!isFromTable) {
      objectPath.set(isUiChanged, ['autocompleteOption', fieldKey], option)
      setIsUiChanged(isUiChanged)
    }
    setAutocomplete(option)
    if (isFromTable) {
      updateMapRowOther(option)
    } else {
      if (updateMapOther) {
        updateMapOther(option)
      }
    }
  }

  const handleBlur = async (value) => {
    if (isFromTable) return
    let designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
    let designerData = objectPath.get(designerPropsData, 'data') || {}
    let datasourceValue = objectPath.get(designContext, `@datasource`) || {}
    let dataset = objectPath.get(jsonForm, 'dataset') || []
    if (!hasOnBlur) return
    let action = uischema.onBlur
    let data = {
      formData: formData,
      allParams: allParams,
      datasourceValue: datasourceValue,
      dataset: dataset,
      designerData: designerData,
    }
    objectPath.set(data, `formData.${uischema.key}`, value)
    objectPath.set(data, `formData.${uischema.name}`, value)
    let { method } = action
    let response
    if (method === 'get') {
      response = await getData(action, data, designContext)
    } else if (method === 'post') {
      response = await postData(action, data, designContext)
    } else if (method === 'put') {
      response = await putData(action, data, designContext)
    } else if (method === 'delete') {
      response = await deleteData(action, data, designContext)
    }
  }

  const updateOtherParam = (autocompleteValue, isOnChange) => {
    let otherValues = objectPath.get(uischema, 'internal.otherValues') || {}
    Object.keys(otherValues).forEach((paramName) => {
      let { field } = objectPath.get(otherValues, [paramName]) || {}
      if (!field) return
      let ui = getCurrentUi(field)
      if (isDynamic) {
        let value = objectPath.get(autocompleteValue, paramName)
        if (isOnChange) {
          let dynamicData = objectPath.get(formData, dynamicName)
          objectPath.set(dynamicData, `${dynamicIndex}.${field}`, value)
          objectPath.set(dynamicData, `${dynamicIndex}.${ui.name}`, value)
          setFieldFormData(`${dynamicName}`, dynamicData)
          objectPath.set(otherParamUpdated, `${field}`, {
            isOnChange,
            value,
            name: field,
            dynamicData,
            dynamicKey,
            dynamicName,
            dynamicIndex,
            isDynamic,
            isMainDynamic: true,
          })
        }
      } else {
        if (ui.key === field) {
          let isUpdate = false
          if (isOnChange) {
            isUpdate = true
          } else {
            let oldValue = objectPath.get(formData, ui.key)
            if (oldValue === undefined) {
              isUpdate = true
            }
          }
          if (!isUpdate) return
          let value = objectPath.get(autocompleteValue, paramName)
          setFieldFormData(ui.key, value)
          setFieldFormData(ui.name, value)
          objectPath.set(formData, ui.key, value)
          objectPath.set(formData, ui.name, value)
          objectPath.set(otherParamUpdated, `${ui.key}`, { value })
          onUpdateCondition(props, designContext, ui.key, formData, {
            isOnChange: true,
          })
        }
      }
    })
    valueKeyList.forEach((paramKey) => {
      let uiName = objectPath.get(valueKey, paramKey)
      let value = objectPath.get(autocompleteValue, paramKey)
      if (isDynamic) {
        let dynamicData = objectPath.get(formData, dynamicName)
        objectPath.set(dynamicData, `${dynamicIndex}.${uiName}`, value)
        setFieldFormData(`${dynamicName}`, dynamicData)
      } else {
        setFieldFormData(uiName, value)
      }
    })
    if (isFromTable) {
      if (updateMapOther) {
        updateMapOther(autocompleteValue)
      }
    }
    checkOtherParamUpdated(otherParamUpdated)
  }

  const checkOtherParamUpdated = (otherParamUpdated) => {
    if (isFromTable) return
    Object.keys(otherParamUpdated).forEach((key) => {
      let { isDynamic } = otherParamUpdated[key]
      if (isDynamic) {
        let status = otherParamUpdated[key]
        onUpdateCondition(props, designContext, key, '', status)
      } else {
        onUpdateCondition(props, designContext, key)
      }
    })
    objectPath.set(isUiChanged, `otherParamUpdated`, otherParamUpdated)
    setIsUiChanged(isUiChanged)
    setOtherParamUpdated(otherParamUpdated)
  }

  const clearOtherParam = () => {
    valueKeyList.forEach((paramKey) => {
      let uiName = objectPath.get(valueKey, paramKey)
      if (isDynamic) {
        setFieldFormData(`${dynamicName}.${dynamicIndex}.${uiName}`, '')
      } else {
        setFieldFormData(uiName, '')
      }
    })
    setIsSelected(false)
  }

  const clearAutocomplete = () => {
    let autocomplete = isMulti ? [] : null
    setAutocomplete(autocomplete)
    setLabelField(undefined)
    clearOtherParam()
  }

  const [autocomplete, setAutocomplete] = useState(isMulti ? [] : null)
  const [isSelected, setIsSelected] = useState(false)

  useEffect(() => {
    let otherValues = objectPath.get(uischema, 'internal.otherValues')
    if (otherValues) {
      let hasOtherValues = Object.keys(otherValues).length > 0
      if (value && hasOtherValues) {
        callAndUpdateOtherValues(value)
      }
    }
  }, [])

  useEffect(() => {
    if (isSelected) {
      updateOtherParam(autocomplete, true)
      setIsSelected(false)
    }
  }, [isSelected])

  useEffect(async () => {
    if (isFromTable) return
    let checkAutocomplete = objectPath.get(isUiChanged, [
      'autocompleteOption',
      fieldKey,
    ])
    if (
      checkAutocomplete &&
      objectPath.get(checkAutocomplete, optionValue) === value
    ) {
      setAutocomplete(checkAutocomplete)
    } else if (value) {
      let autocomplete = await createAutocompleteValue(value)
      setAutocomplete(autocomplete)
    } else {
      clearAutocomplete()
    }
  }, [value])

  useEffect(async () => {
    if (!isFromTable) return
    if (value) {
      let autocomplete = await createAutocompleteRowData(rowUpdate)
      setAutocomplete(autocomplete)
    } else {
      clearAutocomplete()
    }
  }, [value])

  const createAutocompleteRowData = async (rowUpdate) => {
    let keyword
    let autocomplete
    let labelField
    let column = tableColumn.find((item) => item.field === fieldName)
    if (column) {
      let newRowData = {}
      Object.keys(rowUpdate).forEach((key) => {
        if (key.startsWith('@')) return
        let value = objectPath.get(rowUpdate, key)
        objectPath.set(newRowData, key, value)
      })
      let rowFieldValue = getRowFieldValue(column, newRowData)
      keyword = rowFieldValue
      setLabelField(rowFieldValue)
    } else {
      keyword = objectPath.get(rowUpdate, optionValue)
      autocomplete = await updateAutocompleteValue(keyword, true)
      labelField = createLabelField(autocomplete)
      setLabelField(labelField)
    }
    if (autocomplete) {
    } else {
      autocomplete = {
        [optionValue]: keyword,
      }
    }
    if (mapOther) {
      mapOther.forEach(({ field }) => {
        if (objectPath.has(rowUpdate, `@${field}`)) {
          let itemValue = objectPath.get(rowUpdate, `@${field}`)
          objectPath.set(autocomplete, `@${field}`, itemValue)
        }
        if (objectPath.has(autocomplete, field)) {
          let itemValue = objectPath.get(autocomplete, field)
          objectPath.set(rowUpdate, field, itemValue)
        }
      })
      onUpdateRowData(rowUpdate)
    }
    return autocomplete
  }

  const createLabelField = (autocomplete = {}, column = uischema) => {
    let labelField
    if (isUseLabelPattern) {
      labelField = getTitleValue(column, autocomplete)
    } else if (fieldTitle) {
      labelField = getFieldValue(column, autocomplete)
    }
    if (!labelField) {
      return labelField
    }
    if (isFromTable) {
      objectPath.set(autocomplete, `@${optionValue}`, labelField)
      updateData(labelField, `@${optionValue}`)
    } else {
      if (fieldTitle) {
        objectPath.set(autocomplete, `@${optionValue}`, labelField)
        updateData(labelField, `@${optionValue}`)
      }
    }
    setAutocomplete(autocomplete)
    return labelField
  }

  const createLabelOption = (option) => {
    let label = ''
    if (isJsonOption) {
      if (typeof option === 'object') {
        if (jsonLabel && objectPath.has(option, `@${jsonLabel}`)) {
          label = objectPath.get(option, `@${jsonLabel}`)
        } else if (jsonValue && objectPath.has(option, `${jsonLabel}`)) {
          label = objectPath.get(option, `${jsonLabel}`)
        } else if (jsonValue && objectPath.has(option, `@${jsonValue}`)) {
          label = objectPath.get(option, `@${jsonValue}`)
        } else if (jsonValue && objectPath.has(option, `${jsonValue}`)) {
          label = objectPath.get(option, `${jsonValue}`)
        } else if (objectPath.has(option, `@${optionValue}`)) {
          label = objectPath.get(option, `@${optionValue}`)
        }
      } else {
        label = option
      }
    } else if (objectPath.has(option, `@${uischema.field}`)) {
      label = objectPath.get(option, `@${uischema.field}`)
    } else if (objectPath.has(option, `@${optionValue}`)) {
      label = objectPath.get(option, `@${optionValue}`)
    } else {
      label = objectPath.get(option, optionValue)
    }
    if (label === undefined || label === null) return ''
    return label
  }

  const updateAutocompleteValue = async (value, isReturn = false) => {
    if (value) {
      if (isMulti) {
        let newData = []
        value.forEach(async (itemValue) => {
          let found = options.find(
            (option) => objectPath.get(option, optionValue) === itemValue,
          )
          if (found) {
            newData.push(found)
          } else {
            let autocomplete = await getAutocompleteValue(value)
            if (autocomplete) {
              newData.push(autocomplete)
            }
          }
        })
        if (isReturn) {
          return newData
        }
        setAutocomplete(newData)
      } else {
        let found = options.find(
          (option) => objectPath.get(option, optionValue) === value,
        )
        if (found) {
          if (isReturn) {
            return found
          }
          handleSelectPath(found)
        } else {
          let hasValue = objectPath.get(dataValue, optionValue)
          let hasTitle = createLabelField(dataValue)
          if (hasValue && hasTitle && hasValue === value) {
            handleSelectPath(dataValue)
          } else {
            let autocomplete = await getAutocompleteValue(value, {
              isAutoCreate: true,
              isReturn,
            })
            let found =
              objectPath.get(autocomplete, optionValue) === value ||
              objectPath.get(autocomplete, optionValue) == value
            if (found) {
              if (isReturn) {
                return autocomplete
              }
              handleSelectPath(autocomplete)
            } else {
              let autocomplete = {}
              objectPath.set(autocomplete, optionValue, value)
              objectPath.set(autocomplete, `@${optionValue}`, value)
              handleSelectPath(autocomplete)
            }
          }
        }
      }
    } else {
      setAutocomplete(isMulti ? [] : null)
      setLabelField(undefined)
    }
  }

  const createAutocompleteValue = async (value) => {
    if (typeof value === 'number') {
      value = value.toString()
    }
    if (value) {
      if (isMulti) {
        let newData = []
        value.forEach(async (itemValue) => {
          let found = options.find(
            (option) => objectPath.get(option, optionValue) === itemValue,
          )
          if (found) {
            newData.push(found)
          } else {
            let autocomplete = await getAutocompleteValue(value, {
              isReturn: true,
            })
            if (autocomplete) {
              newData.push(autocomplete)
            }
          }
        })
        return newData
      }
      let foundOption = options.find(
        (option) => objectPath.get(option, optionValue) === value,
      )
      if (foundOption) {
        return foundOption
      }
      let hasValue = objectPath.get(dataValue, optionValue)
      let hasTitle = createLabelField(dataValue)
      if (hasValue && hasTitle && hasValue === value) {
        return dataValue
      }
      let autocomplete = await getAutocompleteValue(value, {
        isReturn: true,
      })
      if (autocomplete) {
        let autocompleteValue = objectPath.get(autocomplete, optionValue)
        let foundAutocomplete = compareTwoValue(
          'equalValue',
          autocompleteValue,
          value,
          true,
        )
        if (foundAutocomplete) {
          return autocomplete
        }
      }
      let customAutocomplete = {}
      objectPath.set(customAutocomplete, optionValue, value)
      objectPath.set(customAutocomplete, `@${optionValue}`, value)
      return customAutocomplete
    }
    return isMulti ? [] : null
  }

  const updateInputAndAutocomplete = async (value) => {
    if (value) {
      getAutocompleteValue(value, { isAutoCreate: false })
    } else {
      setLabelField(undefined)
    }
  }

  const updateValue = () => {
    if (isFromTable) return
    let value = objectPath.get(autocomplete, optionValue)
    setIsOnChange(true)
    updateData(value)
  }

  const filterOptions = createFilterOptions({
    limit: limit ? Number(limit) : null,
  })

  const borderRadius = useMemo(() => {
    let topLeft = '5px'
    let topRight = '5px'
    let bottomRight = '5px'
    let bottomLeft = '5px'
    if (prependIcon) {
      topLeft = '0px'
      bottomLeft = '0px'
    }
    if (appendIcon) {
      topRight = '0px'
      bottomRight = '0px'
    }
    return `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`
  }, [prependIcon, appendIcon])

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    [],
  )

  const autocompleteTheme = createTheme({
    overrides: {
      MuiFormControl: {
        root: {
          margin: 0,
        },
      },
      MuiAutocomplete: {
        inputRoot: {
          '&&[class*="MuiOutlinedInput-root"]': {
            borderRadius: borderRadius,
            border: error ? 'red 1px solid' : 'unset',
          },
          '&&[class*="MuiOutlinedInput-root"] $input': {
            padding: 0,
            backgroundColor: 'transparent',
            fontSize: '14px',
            color: colorByTheme,
            height: '18px !important',
            minHeight: '18px !important',
          },
        },
        listbox: {
          backgroundColor: 'white',
          border: 'unset',
          borderRadius: 'unset',
        },
        option: {
          backgroundColor: 'white',
          '&[data-focus="true"]': {
            backgroundColor: '#F2F2F2',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#fff',
          },
        },
      },
    },
  })

  const autocompleteRequire = createTheme({
    overrides: {
      MuiFormControl: {
        root: {
          margin: 0,
        },
      },
      MuiAutocomplete: {
        inputRoot: {
          '&&[class*="MuiOutlinedInput-root"]': {
            borderRadius: borderRadius,
            border: 'red 1px solid',
          },
          '&&[class*="MuiOutlinedInput-root"] $input': {
            padding: 0,
            backgroundColor: 'transparent',
            fontSize: '14px',
            color: colorByTheme,
          },
        },
        listbox: {
          backgroundColor: 'white',
          border: 'unset',
          borderRadius: 'unset',
        },
        option: {
          backgroundColor: 'white',
          '&[data-focus="true"]': {
            backgroundColor: '#F2F2F2',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#fff',
          },
        },
      },
    },
  })

  const isResetField = objectPath.get(isUiChanged, [
    'isResetField',
    uischema.key,
  ])
  useEffect(() => {
    if (isResetField) {
      objectPath.del(isUiChanged, ['isResetField', uischema.key])
      setIsUiChanged(isUiChanged)
      clearAutocomplete()
    }
  }, [isResetField])

  return (
    <InputGroup style={{ flexWrap: 'unset' }}>
      <InputPrepend
        isReadOnly={isReadOnly}
        uischema={uischema}
        prependIcon={prependIcon}
      />
      <MuiThemeProvider
        theme={showRequire ? autocompleteRequire : autocompleteTheme}
      >
        <Autocomplete
          id={fieldKey}
          name={fieldName}
          loading={loading}
          onOpen={() => {
            if (!labelField) {
              handleInputPath(labelField)
            }
          }}
          onClose={() => {
            setLoading(false)
          }}
          className="w-100 p-0"
          PopperComponent={(props) => (
            <Popper
              {...props}
              style={{ maxWidth: 'fit-content' }}
              placement="bottom-start"
            />
          )}
          autoHighlight
          freeSolo
          filterOptions={filterOptions}
          options={options}
          multiple={isMulti}
          filterSelectedOptions
          getOptionLabel={(option) => createLabelOption(option)}
          value={autocomplete || null}
          inputValue={(labelField || '').toString()}
          onInputChange={(event, newInputValue) => {
            setLabelField(newInputValue)
          }}
          onChange={(e, value) => {
            if (hasOnBlur && value) {
              let selectedValue = objectPath.get(value, optionValue)
              if (isInput) {
                objectPath.set(value, optionValue, labelField)
              } else {
                objectPath.set(value, optionValue, selectedValue)
              }
              updateOtherParam(value, true)
              handleSelectPath(value)
              setIsSelected(true)
              setIsInput(false)
            } else {
              let freeSolo = typeof value === 'string'
              handleSelectPath(value, freeSolo)
              updateOtherParam(value, true)
            }
            if (setIsOnChange) {
              setIsOnChange(true)
            }
          }}
          onBlur={(e) => {
            let blurValue = e.target.value
            if (hasOnBlur) {
              handleBlur(blurValue)
              setIsSelected(true)
            }
            updateValue()
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={uischema?.fieldName}
              multiline
              onChange={(e) => {
                setIsInput(true)
                setLabelField(e.target.value)
                setAutocomplete({
                  [optionValue]: e.target.value,
                  [`@${optionValue}`]: e.target.value,
                })
                debounceCallback(
                  updateInputAndAutocomplete,
                  [e.target.value],
                  'on-update-autocomplete',
                )
              }}
              style={{ background: isReadOnly ? '#e9ecef' : '' }}
              readonly={isReadOnly}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              if (hasOnBlur) {
                setIsSelected(true)
              }
              if (!isFromTable) {
                handleEnterAction(ev, uischema, designContext, config)
                ev.preventDefault()
                ev.stopPropagation()
              }
            }
          }}
          disabled={isReadOnly}
        />
      </MuiThemeProvider>
      <InputAppend
        isReadOnly={isReadOnly}
        uischema={uischema}
        appendIcon={appendIcon}
        iconTooltip={appendIconTooltip}
      />
    </InputGroup>
  )
}

export default AutocompleteContent
